import React, {useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useCancelableEffect} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {ApiMessageDataGroup} from '../../../models/api/dataGroupModel';
import {DraftMessageModel} from '../../../models/api/messageModel';
import {fetchApiAsync} from '../../../services/http/http';
import {findDraftMessageInDataGroup} from '../../../store/data/messages/saveMessageActions';
import {getFindTeammate} from '../../../store/data/teammates/lazyTeammatesSelectors';
import {ComposerPanelId, ViewerPanelId} from '../../../store/panels/panelStateBase';
import {Dialog} from '../../common/dialogs/dialog';
import {DialogHeader} from '../../common/dialogs/dialogHeader';
import {ComposerResolveConflictsModalContent} from './composerResolveConflictsModalContent';

/*
 * Props.
 */

interface ComposerResolveConflictsModalProps {
  panelId: ComposerPanelId | ViewerPanelId;
  message: DraftMessageModel;
  onModalClose: Handler;
  onResolveLatest: Handler;
  onDiscardLatest: HandlerOf<string | undefined>;
}

const StyledDialog = styled(Dialog)`
  margin: 0 20px;
`;

/*
 * Component.
 */

export const ComposerResolveConflictsModal = ({
  message,
  panelId,
  onModalClose,
  onResolveLatest,
  onDiscardLatest,
}: ComposerResolveConflictsModalProps) => {
  const [serverMessage, setServerMessage] = useState<DraftMessageModel | undefined>(undefined);
  const [hasError, setHasError] = useState(false);
  const findTeammate = useAppSelector(getFindTeammate);

  useCancelableEffect(
    (cancelToken) => {
      (async () => {
        try {
          const serverDataGroup = await fetchApiAsync(ApiMessageDataGroup, message.url, {
            cancelToken,
          });
          const serverMessagefromDataGroup = findDraftMessageInDataGroup(serverDataGroup, message.uid);
          if (serverMessagefromDataGroup) {
            setServerMessage(serverMessagefromDataGroup);
          } else {
            setHasError(true);
          }
        } catch {
          setHasError(true);
        }
      })();
    },
    [message.url, message.uid],
  );

  return (
    <StyledDialog width={800} onRequestClose={onModalClose}>
      <DialogHeader title={<Trans id="latest-version">Latest version</Trans>} onRequestClose={onModalClose} />
      <ComposerResolveConflictsModalContent
        message={serverMessage}
        findTeammate={findTeammate}
        hasError={hasError}
        panelId={panelId}
        onResolveLatest={onResolveLatest}
        onDiscardLatest={onDiscardLatest}
      />
    </StyledDialog>
  );
};
